import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { createSafeCloseHandler } from '../../utils/modalUtils';

interface AddActionItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItem: (itemData: any) => Promise<void>;
  projectData: ProjectInterface;
  users: any[];
}

interface ActionItemFormData {
  name: string;
  description: string;
  owner_id: string;
  due_date: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD';
}

export const AddActionItemModal: React.FC<AddActionItemModalProps> = ({
  isOpen,
  onClose,
  onAddItem,
  projectData,
  users,
}) => {
  const { keycloak: { idTokenParsed } } = useKeycloak();
  
  // Find current user in project users by matching email
  const currentUser = users.find(user => user.email === idTokenParsed?.email);
  const currentUserId = currentUser?.id || '';
  
  const [formData, setFormData] = useState<ActionItemFormData>({
    name: '',
    description: '',
    owner_id: currentUserId, // Auto-set current user as owner
    due_date: new Date().toISOString().split('T')[0],
    priority: 'LOW',
    status: 'NOT_STARTED'
  });
  const [isLoading, setIsLoading] = useState(false);

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['owner_id', 'due_date', 'priority', 'status'] // Exclude auto-filled fields
  );

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Update owner_id when modal opens or users change
  useEffect(() => {
    if (isOpen && currentUserId && formData.owner_id !== currentUserId) {
      setFormData(prev => ({
        ...prev,
        owner_id: currentUserId
      }));
    }
  }, [isOpen, currentUserId, formData.owner_id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Action item name is required');
      return;
    }
    if (!formData.status) {
      toast.error('Status is required');
      return;
    }

    setIsLoading(true);
    try {
      const baseData = {
        name: formData.name,
        description: formData.description,
        priority: formData.priority,
        status: formData.status,
        project_id: projectData.id,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : undefined,
      };
      
      // Only add owner_id if it has a valid UUID value
      const itemData: any = { ...baseData };
      if (formData.owner_id && formData.owner_id.trim()) {
        itemData.owner_id = formData.owner_id;
      }
      
      console.log('AddActionItemModal: submitting item data', itemData);
      
      await onAddItem(itemData);
      
      console.log('AddActionItemModal: action item added successfully');
      toast.success('Action item added successfully');
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        owner_id: currentUserId, // Keep current user as owner
        due_date: new Date().toISOString().split('T')[0],
        priority: 'LOW',
        status: 'NOT_STARTED'
      });
      
      onClose();
    } catch (error) {
      console.error('AddActionItemModal: failed to add action item', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add action item';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={handleSafeClose}
    >
      <button
        onClick={() => handleSafeClose()}
        className="fixed top-4 right-4 z-[60] text-gray-400 hover:text-gray-600 p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Add New Action Item</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action Item Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner
              </label>
              <select
                name="owner_id"
                value={formData.owner_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select owner</option>
                {(users || []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="NOT_STARTED">Not Started</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="ON_HOLD">On Hold</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? 'Adding...' : 'Add Action Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 